import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { ProviderMyProfileSubmit } from '@brands/Dashboard/MyProfile/Provider/utils/types';
import {
  ClientDivisionGroup,
  ClientDivisionOption,
  getAllowedDivisionIds,
  getAssignedClientDivisionGroups,
  getClientDivisionOptionGroups,
  scopeDivisionGroupsToClientAdmin,
} from '@brands/services/identity/clientDivisionOptions';
import { NetworkOption } from '@brands/services/identity/networkOptions';
import { ClientTag, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { checkAndSetDate } from '@brands/Utils/checkAndSetDate';
import { OptionInsurance, OptionLanguage, licenseOptions, stateOptions } from '@brands/Utils/selectOptions';
import { checkExpiredDate, checkValidDate, isValidDate } from '@brands/Utils/validation';
import { Autocomplete, Box, Link, TextField as MuiTextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { IoIosAddCircle } from 'react-icons/io';
import { TiDelete } from 'react-icons/ti';

import styles from '../styles.module.scss';

type ProviderProfileAdminSectionProps = {
  watch: UseFormWatch<ProviderMyProfileSubmit>;
  register: UseFormRegister<ProviderMyProfileSubmit>;
  errors: any;
  userRoleName: string;
  myProfileForm: ProviderMyProfileSubmit;
  insurancePlanName: OptionInsurance[];
  setValue?: UseFormSetValue<ProviderMyProfileSubmit>;
  getValues?: UseFormGetValues<ProviderMyProfileSubmit>;
  clientTagOptions?: OptionLanguage[];
  clientTagCatalog?: ClientTag[];
  clientTagsRequired?: boolean;
  networkOptions?: NetworkOption[];
  /** True when the actor may view but not manage this profile (D1 Network staff seen by a Client Admin). */
  isManagedByD1?: boolean;
  /** True when the actor is a Client Admin, who never changes a user's client tags. */
  clientTagsLocked?: boolean;
  /** The signed-in user for division scoping; null while a Client Admin's own divisions load. */
  divisionScopeActor?: Pick<UserProfile, 'role' | 'divisions'> | null;
};

type InsuranceCarrierFormValue = ProviderMyProfileSubmit['licences'][number]['insurance_carriers'][number] & {
  _tempId?: string;
};

type LicenceFormValue = ProviderMyProfileSubmit['licences'][number] & {
  _tempId?: string;
};

const ProviderProfileAdminSection = ({
  watch,
  register,
  errors,
  userRoleName,
  myProfileForm,
  insurancePlanName,
  setValue,
  getValues,
  clientTagOptions = [],
  clientTagCatalog = [],
  clientTagsRequired = false,
  networkOptions = [],
  isManagedByD1 = false,
  clientTagsLocked = false,
  divisionScopeActor = null,
}: ProviderProfileAdminSectionProps): JSX.Element => {
  const isOwnProviderView = userRoleName === 'Provider';
  // Both the provider looking at their own admin-managed data and a Client Admin looking at
  // D1 Network staff get the same view-only rendering; only the notice below differs.
  const isReadOnly = isOwnProviderView || isManagedByD1;
  const isClientTagsReadOnly = isReadOnly || clientTagsLocked;
  const canEditNetwork = userRoleName === UserRoleName.Admin || userRoleName === UserRoleName.SuperAdmin;
  const watchedClientTags = watch('client_tags');
  const selectedClientTagIds = (Array.isArray(watchedClientTags) ? watchedClientTags : []).map((tag) => tag.id);
  const watchedClientDivisions = watch('client_divisions');
  const selectedClientDivisions = Array.isArray(watchedClientDivisions) ? watchedClientDivisions : [];
  const divisionGroups = useMemo(
    () =>
      scopeDivisionGroupsToClientAdmin(
        getClientDivisionOptionGroups(
          clientTagCatalog,
          selectedClientTagIds,
          (myProfileForm.client_divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
        ),
        clientTagCatalog,
        divisionScopeActor,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientTagCatalog, JSON.stringify(selectedClientTagIds), myProfileForm.client_divisions, divisionScopeActor],
  );

  const assignedDivisionGroups = useMemo(
    () => getAssignedClientDivisionGroups(clientTagCatalog, myProfileForm.client_divisions || []),
    [clientTagCatalog, myProfileForm.client_divisions],
  );

  // Each picker owns only its own tag's divisions; the rest of the field rides along.
  const setGroupDivisions = (group: ClientDivisionGroup, selected: ClientDivisionOption[]): void => {
    const groupIds = new Set(group.options.map((option) => String(option.id)));

    setValue?.(
      'client_divisions',
      [
        ...selectedClientDivisions.filter((division) => !groupIds.has(String(division.id))),
        ...selected.map((option) => ({ id: Number(option.id), name: option.label })),
      ],
      { shouldValidate: true },
    );
  };
  // For editing: use live RHF state so changes are tracked without touching myProfileForm.
  // For read-only: fall back to myProfileForm (RHF may not be populated yet).
  const displayLicences = isReadOnly ? myProfileForm.licences : watch('licences') ?? myProfileForm.licences;
  const insuranceCarrierOptions = React.useMemo(
    () => insurancePlanName.map((option) => ({ ...option, value: String(option.value) })),
    [insurancePlanName],
  );

  const getInsuranceCarrierId = (carrier: {
    insurance_carrier_id?: number;
    insurance_carrier?: { id?: number };
  }): string => String(carrier.insurance_carrier_id ?? carrier.insurance_carrier?.id ?? '');

  const getInsuranceCarrierLabel = (carrier: {
    insurance_carrier_id?: number;
    insurance_carrier?: { id?: number; name?: string };
  }): string => {
    const carrierId = getInsuranceCarrierId(carrier);

    return (
      insuranceCarrierOptions.find((option) => option.value === carrierId)?.label ||
      carrier.insurance_carrier?.name ||
      carrierId
    );
  };

  const createEnrollmentTempId = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const getEnrollmentKey = (carrier: InsuranceCarrierFormValue): string =>
    String(carrier._tempId ?? carrier.insurance_carrier_id ?? carrier.insurance_carrier?.id);

  const addLicenceRow = (): void => {
    const current = getValues?.('licences') ?? [];
    setValue?.('licences', [
      ...current,
      {
        state: '',
        number: '',
        expiry_date: '',
        insurance_carriers: [],
        _tempId: createEnrollmentTempId(),
      } as ProviderMyProfileSubmit['licences'][number],
    ]);
  };

  const removeLicenceRow = (index: number): void => {
    const current = getValues?.('licences') ?? [];
    setValue?.(
      'licences',
      current.filter((_, i) => i !== index),
    );
  };

  const addEnrollmentRow = (licenceIndex: number): void => {
    const current = getValues?.('licences') ?? [];
    setValue?.(
      'licences',
      current.map((l, i) => {
        if (i !== licenceIndex) return l;
        return {
          ...l,
          insurance_carriers: [
            ...(l.insurance_carriers ?? []),
            { effective_date: '', recredential_date: '', _tempId: createEnrollmentTempId() },
          ],
        };
      }),
    );
  };

  const removeEnrollmentRow = (licenceIndex: number, enrollmentIndex: number): void => {
    const current = getValues?.('licences') ?? [];
    setValue?.(
      'licences',
      current.map((l, i) => {
        if (i !== licenceIndex) return l;
        return {
          ...l,
          insurance_carriers: (l.insurance_carriers ?? []).filter((_, ci) => ci !== enrollmentIndex),
        };
      }),
    );
  };

  const getCarrierValidationError = (carrier: InsuranceCarrierFormValue): string | null => {
    const carrierId = getInsuranceCarrierId(carrier);
    if (!carrierId || carrierId === '0') return 'Please select an insurance carrier.';

    const { effective_date, recredential_date } = carrier;

    if (!effective_date) return 'Effective date is required.';
    const effParts = effective_date.split('/');
    if (!isValidDate(effective_date) || !checkValidDate(effParts[0], effParts[1], effParts[2])) {
      return 'Please enter a valid effective date (MM/DD/YYYY).';
    }
    if (Number(effParts[2]) < 1950) {
      return 'Effective date year must be 1950 or later.';
    }

    if (!recredential_date) return 'Re-credentialing date is required.';
    const recParts = recredential_date.split('/');
    if (!isValidDate(recredential_date) || !checkValidDate(recParts[0], recParts[1], recParts[2])) {
      return 'Please enter a valid re-credentialing date (MM/DD/YYYY).';
    }
    if (checkExpiredDate(recParts[0], recParts[1], recParts[2])) {
      return 'Re-credentialing date must be a future date.';
    }

    return null;
  };

  return (
    <Box className={`${styles.sectionWrapperAdminView} ${styles.formContainer}`}>
      <Typography className={styles.sectionTitle}>Admin-Managed Information</Typography>

      {isOwnProviderView && (
        <Typography className={styles.sectionDescription}>
          The information in this section is managed by our admin team. While you can view it, edits cannot be made
          directly here. If any details need to be updated, please email{' '}
          <Link
            href="https://mail.google.com/mail/?view=cm&fs=1&to=providers@dentistry.one"
            target="_blank"
            rel="noopener noreferrer"
          >
            providers@dentistry.one
          </Link>
          .
        </Typography>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Typography className={styles.subTitle}>Licensure</Typography>
        {!isReadOnly && (
          <button
            type="button"
            onClick={addLicenceRow}
            style={{ color: '#3498db', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}
          >
            <IoIosAddCircle /> Add License
          </button>
        )}
      </div>

      {displayLicences?.map((licence, index) => (
        <div
          key={(licence as LicenceFormValue).id ?? (licence as LicenceFormValue)._tempId ?? index}
          style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <div className={styles.inputWrapRow} style={{ position: 'relative' }}>
            <div className={styles.inputWrap}>
              {isReadOnly ? (
                <TextField
                  {...register(`licences.${index}.state`)}
                  name={`licences.${index}.state`}
                  label="State of license"
                  value={licence.state}
                  disabled
                  errors={errors}
                  required
                  inputClass={styles.profileInputClass}
                  wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                />
              ) : (
                <MUISelectInput
                  {...register(`licences.${index}.state`)}
                  name={`licences.${index}.state`}
                  label="State of license"
                  options={stateOptions}
                  required
                  errors={errors}
                  value={licence.state || ''}
                  onChange={(value: string) => {
                    setValue?.(`licences.${index}.state`, value, { shouldValidate: true });
                  }}
                  wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                />
              )}
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register(`licences.${index}.number`)}
                name={`licences.${index}.number`}
                label="License #"
                value={licence.number}
                disabled={isReadOnly}
                errors={errors}
                required
                onChange={
                  isReadOnly
                    ? undefined
                    : (e) => setValue?.(`licences.${index}.number`, e.target.value, { shouldValidate: true })
                }
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register(`licences.${index}.expiry_date`)}
                name={`licences.${index}.expiry_date`}
                label="Expiry date"
                value={licence.expiry_date}
                disabled={isReadOnly}
                errors={errors}
                required
                maxLength={10}
                onChange={
                  isReadOnly
                    ? undefined
                    : (e) => {
                        const dateValue = checkAndSetDate(e);
                        setValue?.(`licences.${index}.expiry_date`, dateValue, { shouldValidate: true });
                      }
                }
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            {!isReadOnly && (displayLicences?.length ?? 0) > 1 && (
              <button
                type="button"
                onClick={() => removeLicenceRow(index)}
                style={{ alignSelf: 'center', color: '#e74c3c', marginLeft: '8px' }}
                aria-label="Remove license"
              >
                <TiDelete size={20} />
              </button>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: '8px',
            }}
          >
            <Typography className={styles.subTitleSmall}>
              Enrollments ({stateOptions.find((s) => s.value === licence.state)?.label || licence.state})
            </Typography>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => addEnrollmentRow(index)}
                style={{ color: '#3498db', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}
              >
                <IoIosAddCircle /> Add Insurance
              </button>
            )}
            {isReadOnly && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}></div>}
          </div>

          {licence.insurance_carriers?.map((carrier, carrierIndex) => (
            <div
              key={getEnrollmentKey(carrier as InsuranceCarrierFormValue)}
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              <div
                key={getEnrollmentKey(carrier as InsuranceCarrierFormValue)}
                className={styles.inputWrapRow}
                style={{ position: 'relative' }}
              >
                <div className={styles.inputWrap}>
                  {isReadOnly ? (
                    <TextField
                      name={`licences.${index}.insurance_carriers.${carrierIndex}.insurance_carrier_id`}
                      label="Insurance Carrier"
                      value={getInsuranceCarrierLabel(carrier)}
                      disabled
                      errors={errors}
                      required
                      inputClass={styles.profileInputClass}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                    />
                  ) : (
                    <MUISelectInput
                      name={`licences.${index}.insurance_carriers.${carrierIndex}.insurance_carrier_id`}
                      label="Insurance Carrier"
                      options={insuranceCarrierOptions}
                      required
                      errors={errors}
                      value={getInsuranceCarrierId(carrier)}
                      onChange={(value: string) => {
                        setValue?.(
                          `licences.${index}.insurance_carriers.${carrierIndex}.insurance_carrier_id`,
                          Number(value),
                          { shouldValidate: true },
                        );
                      }}
                      wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                    />
                  )}
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register(`licences.${index}.insurance_carriers.${carrierIndex}.recredential_date`)}
                    name={`licences.${index}.insurance_carriers.${carrierIndex}.recredential_date`}
                    label="Re-credentialing date"
                    value={carrier.recredential_date}
                    disabled={isReadOnly}
                    errors={errors}
                    required
                    maxLength={10}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => {
                            const dateValue = checkAndSetDate(e);
                            setValue?.(
                              `licences.${index}.insurance_carriers.${carrierIndex}.recredential_date`,
                              dateValue,
                              { shouldValidate: true },
                            );
                          }
                    }
                    inputClass={styles.profileInputClass}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register(`licences.${index}.insurance_carriers.${carrierIndex}.effective_date`)}
                    name={`licences.${index}.insurance_carriers.${carrierIndex}.effective_date`}
                    label="Effective date"
                    value={carrier.effective_date}
                    disabled={isReadOnly}
                    errors={errors}
                    required
                    maxLength={10}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => {
                            const dateValue = checkAndSetDate(e);
                            setValue?.(
                              `licences.${index}.insurance_carriers.${carrierIndex}.effective_date`,
                              dateValue,
                              {
                                shouldValidate: true,
                              },
                            );
                          }
                    }
                    inputClass={styles.profileInputClass}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => removeEnrollmentRow(index, carrierIndex)}
                    style={{ alignSelf: 'center', color: '#e74c3c', marginLeft: '8px' }}
                    aria-label="Remove enrollment"
                  >
                    <TiDelete size={20} />
                  </button>
                )}
              </div>
              {!isReadOnly &&
                (() => {
                  const error = getCarrierValidationError(carrier as InsuranceCarrierFormValue);
                  if (!error) return null;
                  return (
                    <Typography style={{ color: '#d32f2f', fontSize: '12px', marginTop: '-8px' }}>{error}</Typography>
                  );
                })()}
            </div>
          ))}
        </div>
      ))}

      <Typography className={styles.subTitle}>NPI and credentials</Typography>

      <div className={styles.inputWrapRow}>
        <div className={styles.inputWrap}>
          <TextField
            {...register('national_provider_id')}
            name="national_provider_id"
            label="Provider NPI #"
            value={watch('national_provider_id')}
            disabled={isReadOnly}
            errors={errors}
            required
            onChange={
              isReadOnly
                ? undefined
                : (e) => setValue?.('national_provider_id', e.target.value, { shouldValidate: true })
            }
            inputClass={styles.profileInputClass}
            wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
          />
        </div>
        <div className={styles.inputWrap}>
          {isReadOnly ? (
            <TextField
              {...register('credentials')}
              name="credentials"
              label="Credentials (DDS/DMD/RDH)"
              value={watch('credentials')}
              disabled
              errors={errors}
              required
              inputClass={styles.profileInputClass}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
            />
          ) : (
            <MUISelectInput
              {...register('credentials')}
              name="credentials"
              label="Credentials (DDS/DMD/RDH)"
              options={licenseOptions}
              required
              errors={errors}
              value={watch('credentials') || ''}
              onChange={(value: string) => {
                if (value) setValue?.('credentials', value, { shouldValidate: true });
              }}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
            />
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Typography className={styles.subTitle} style={{ marginBottom: '20px' }}>
          Other
        </Typography>

        {isClientTagsReadOnly ? (
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                name="client_tags"
                label="Client tags"
                value={myProfileForm.client_tags?.map((clientTag) => clientTag.name)?.join(', ') || ''}
                disabled
                errors={errors}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
        ) : (
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <input type="hidden" {...register('client_tags')} />

              <Autocomplete
                multiple
                disablePortal
                options={clientTagOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={clientTagOptions.filter((option) =>
                  selectedClientTagIds.some((tagId) => Number(tagId) === Number(option.id)),
                )}
                onChange={(_, selectedOptions) => {
                  setValue?.(
                    'client_tags',
                    selectedOptions.map((option) => ({
                      id: Number(option.id),
                      name: option.label,
                    })),
                    {
                      shouldValidate: true,
                    },
                  );
                  const allowedDivisionIds = getAllowedDivisionIds(
                    clientTagCatalog,
                    selectedOptions.map((option) => option.id),
                    (myProfileForm.client_divisions || []).map((d) => ({
                      id: String(d.id),
                      value: d.id,
                      label: d.name,
                    })),
                  );
                  setValue?.(
                    'client_divisions',
                    selectedClientDivisions.filter((division) => allowedDivisionIds.has(String(division.id))),
                    { shouldValidate: true },
                  );
                }}
                renderOption={(optionProps, option) => {
                  const optionClassName = `${String(optionProps.className ?? '')} fs-exclude`.trim();

                  return (
                    <li {...optionProps} key={option.id} className={optionClassName}>
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    required={clientTagsRequired}
                    label="Client tags"
                    error={Boolean(errors.client_tags)}
                    helperText={errors.client_tags?.message}
                    className={`fs-unmask ${styles.completeProfileClass}`}
                    inputProps={{
                      ...params.inputProps,
                      className: `${params.inputProps.className || ''} fs-exclude`,
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}

        {isReadOnly ? (
          <>
            {(assignedDivisionGroups.length > 0
              ? assignedDivisionGroups
              : [{ clientTagId: '', clientTagName: '', options: [] }]
            ).map((group) => (
              <div className={styles.inputWrapRow} key={group.clientTagId || group.clientTagName}>
                <div className={styles.inputWrap}>
                  <TextField
                    name="client_divisions"
                    label={group.clientTagName ? `${group.clientTagName} - Division(s)` : 'Division(s)'}
                    value={group.options.map((division) => division.label).join(', ')}
                    disabled
                    errors={errors}
                    inputClass={styles.profileInputClass}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
              </div>
            ))}
          </>
        ) : (
          selectedClientTagIds.length > 0 &&
          divisionGroups.length > 0 && (
            <>
              <input type="hidden" {...register('client_divisions')} />

              {divisionGroups.map((group) => (
                <div className={styles.inputWrapRow} key={group.clientTagId}>
                  <div className={styles.inputWrap}>
                    <Autocomplete
                      multiple
                      disablePortal
                      options={group.options}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={group.options.filter((option) =>
                        selectedClientDivisions.some((division) => Number(division.id) === Number(option.id)),
                      )}
                      onChange={(_, selectedOptions) => setGroupDivisions(group, [...selectedOptions])}
                      renderOption={(optionProps, option) => {
                        const optionClassName = `${String(optionProps.className ?? '')} fs-exclude`.trim();

                        return (
                          <li {...optionProps} key={option.id} className={optionClassName}>
                            {option.label}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          label={`${group.clientTagName} - Division(s)`}
                          InputLabelProps={{ ...params.InputLabelProps, required: Boolean(group.required) }}
                          className={`fs-unmask ${styles.completeProfileClass}`}
                          inputProps={{
                            ...params.inputProps,
                            className: `${params.inputProps.className || ''} fs-exclude`,
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              ))}
            </>
          )
        )}
        {canEditNetwork && (
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MUISelectInput
                name="network"
                label="Network"
                options={networkOptions.map((option) => ({ id: option.code, value: option.code, label: option.label }))}
                errors={errors}
                value={watch('network') || ''}
                onChange={(value: string) => {
                  setValue?.('network', value || null, { shouldValidate: true });
                }}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default ProviderProfileAdminSection;
